// @flow

import ObservableMap from 'observable-map';

import _ from 'lodash';
import stringify from 'json-stable-stringify';

import { filterFromDictionary } from 'dictionary-helpers';
import { compactJoin } from 'common-utils';
import extendedKladrUtils from 'extended-kladr-utils';
import OSAGO_POLICY_SERIES from 'known-osago-policy-series';

import {
	ExtendedSettlementsDataSet,
	ExtendedStreetsDataSet,
	ExtendedHousesDataSet,
} from 'rgs-datasets2';

export type Data = {
	regions: Array<Object>,
	categories: Array<Object>,
	facilityTypes: Array<Object>,
	wayAddressingTypes: Array<Object>,
	accidentDamageKinds: Array<Object>,
};

export default class FeedbackDictionaries {
	regions: ObservableMap
	vehicleMakers: ObservableMap
	vehicleModels: ObservableMap
	themes: Object
	categoryTypes: Object
	categories: Object
	facilityTypes: Array<Object>
	wayAddressingTypes: Array<Object>
	accidentDamageKinds: Object
	personTypes: Object
	osagoPolicySeries: Array<string>
	vehicleIdentificatorType: Object
	responseByType: Object
	rgsPhoneTypes: Object
	accidentDamageTypes: Object
	rgsOfficeCities: ObservableMap
	rgsOfficeDivisions: ObservableMap
	calculatorMode: Object

	constructor () {
		this.kladr = {
			housesHashMap: new ObservableMap({ hash: (v) => stringify(v) }),
			streets: new ObservableMap,
			settlements: new ObservableMap({ hash: (v) => stringify(v) }),
			zipCode: new ObservableMap,
			zipCodeHashMap: new ObservableMap({ hash: (v) => stringify(v) }),
			regions: new ObservableMap({ hash: (v) => stringify(v) }),
		};

		this.datasets = {
			settlements: () => new ExtendedSettlementsDataSet({
				display: 'settlementWithArea',
				source: {
					remote: {
						query: (search) => ({ search }),
						transform: response => {
							_.each(response, (record) => {
								const region: Region = this.kladr.regions.get(record.RegionID);

								const isNeedRegionName = !extendedKladrUtils.isFederalSettlement(
									record.Kladr,
								);

								record.settlementWithArea = compactJoin(...[
									record.Name,
									record.CityName,
									record.AreaName,
									isNeedRegionName ? region.Name : null,
								]);

								this.kladr.settlements.biset(
									record.settlementWithArea,
									record.Kladr,
									true,
								);

								this.kladr.settlements.biset(
									{
										AreaName: record.AreaName,
										CityName: record.CityName,
										Name: record.Name,
									},
									record.Kladr,
									true,
								);

								this.kladr.zipCode.setAbsent(
									record.Kladr, record.PostCode, true,
								);

							});

							this.kladr.settlements.notify();
							this.kladr.zipCode.notify();

							return response;
						},
					},
				},
				limit: 100,
			}),

			streetsForSettlement: (settlementAccessor) => new ExtendedStreetsDataSet({
				source: {
					remote: {
						query: (search) => {
							const settlementKladr = settlementAccessor();
							return settlementKladr && { search, settlementKladr };
						},
						transform: response => {
							_.each(response, (record) => {
								this.kladr.streets.biset(record.Kladr, record.Name, true);
								this.kladr.zipCode.setAbsent(
									record.Kladr, record.PostCode, true,
								);
							});

							this.kladr.streets.notify();
							this.kladr.zipCode.notify();

							return settlementAccessor() && response;
						},
					},
				},
				limit: 100,
			}),

			// eslint-disable-next-line max-len
			houses: (settlementAccessor, streetAccessor, hasStreetAccessor) => new ExtendedHousesDataSet({
				source: {
					remote: {
						query: (search) => {
							const settlementKladr = settlementAccessor();
							const streetKladr = streetAccessor();
							const hasStreet = hasStreetAccessor();

							if ((hasStreet && streetKladr) || (!hasStreet && settlementKladr)) {
								return { search, settlementKladr, streetKladr, hasStreet };
							}
						},
						transform: response => {
							_.each(response, (record) => {
								this.kladr.housesHashMap.set(
									{
										OuterKladr: hasStreetAccessor()
											? streetAccessor()
											: settlementAccessor(),
										Name: record.Name,
									},
									record.Kladr,
									true);
								this.kladr.zipCodeHashMap.set(
									{ Kladr: record.Kladr, Name: record.Name },
									record.PostCode,
									true,
								);
							});

							this.kladr.housesHashMap.notify();
							this.kladr.zipCode.notify();
							this.kladr.zipCodeHashMap.notify();

							return hasStreetAccessor()
								? (streetAccessor() && response)
								: (settlementAccessor() && response);
						},
					},
				},
				limit: 100,
			}),
		};

		this.valueFilters = {
			settlements: filterFromDictionary(this.kladr.settlements),
			streets: filterFromDictionary(this.kladr.streets),
			houses: () => true,
		};

		this.regions = new ObservableMap();

		this.vehicleMakers = new ObservableMap();

		this.vehicleModels = new ObservableMap();

		this.rgsOfficeCities = new ObservableMap();

		this.rgsOfficeDivisions = new ObservableMap();

		this.categories = {};

		this.facilityTypes = [];

		this.wayAddressingTypes = [];

		this.accidentDamageKinds = {};

		this.themes = {
			claimSettlement: {
				name: 'Урегулирование убытка',
				id: 'ClaimSettlement',
			},
			eOsago: {
				name: 'ОСАГО',
				id: 'EOsago',
			},
			kbm: {
				name: 'КБМ по ОСАГО',
				id: 'Kbm',
			},
			other: {
				name: 'Другое',
				id: 'Other',
			},
			salesAndOrders: {
				name: 'Продажи и сопровождение договоров',
				id: 'SalesAndOrders',
			},
			health: {
				name: 'Здоровье',
				description: '(ДМС, Телемедицина, Онкозащита, Защита от клеща и т.п.)',
				id: 'Health',
			},
		};

		this.categoryTypes = {
			service: {
				id: 'Service',
			},
			category: {
				id: 'Category',
			},
			subcategory: {
				id: 'Subcategory',
			},
			dmsCategory: {
				id: 'Health',
			},
		};

		this.personTypes = {
			physical: {
				id: "Physical",
				name: "Физическое лицо",
			},
			juridical: {
				id: "Juridical",
				name: "Юридическое лицо",
			},
		};

		this.osagoPolicySeries = OSAGO_POLICY_SERIES;

		this.actualOsagoPolicySeries = _.without(OSAGO_POLICY_SERIES, 'ААА');

		this.vehicleIdentificatorType = {
			vin: {
				id: "Vin",
				name: "VIN",
			},
			body: {
				id: "BodyNumber",
				name: "Кузов",
			},
			chassis: {
				id: "ChassisNumber",
				name: "Шасси",
			},
		};

		this.responseByType = {
			email: {
				id: "Email",
				name: "На email",
			},
			post: {
				id: "Post",
				name: "Обычной почтой",
			},
		};

		this.rgsPhoneTypes = {
			first: {
				id: "First",
				value: "8-800-200-0-900 или 0530",
			},
			second: {
				id: "Second",
				value: "8-800-200-99-77 или (495) 926-99-77",
			},
			other: {
				id: "Other",
				value: "Другое",
			},
		};
		
		this.calculatorMode = {
			kbmCheck: 'kbmCheck',
		};
	}

	loadData (data: Data) {
		_.each(data.regions, region => {
			this.regions.setAbsent(parseInt(region.id), region, true);
		});
		this.regions.notify();

		_.each(data.kladrRegions, (region) => {
			this.kladr.regions.setAbsent(region.ID, region);
		});

		Object.assign(this, {
			categories: data.categories,
			facilityTypes: data.facilityTypes,
			wayAddressingTypes: data.wayAddressingTypes,
			accidentDamageKinds: data.accidentDamageKinds,
		});
	}
}
